import {
  Box,
  Heading,
  VStack,
  Text,
  Button,
  HStack,
  Badge,
  useToast,
} from "@chakra-ui/react"
import { Plus } from "lucide-react"
import { useEffect, useState } from "react"
import useNotificationSubscriptionStore from "@/store/NotificationSubscriptionStore"
import useNotificationChannelStore from "@/store/NotificationChannelStore"
import SelectNotificationChannelDialog from "./SelectNotificationChannelDialog"

interface NotificationChannelListProps {
  featureCode: string
  id: string
  onChannelsChange?: (channelIds: string[]) => void
}

interface Channel {
  id: string
  name: string
  type: string
  configuration: Record<string, any>
  isActive: boolean
}

const getChannelTypeColor = (type: string) => {
  switch (type.toLowerCase()) {
    case "slack":
      return "purple"
    case "discord":
      return "blue"
    case "line":
      return "green"
    case "email":
      return "orange"
    default:
      return "gray"
  }
}

export default function NotificationChannelList({
  featureCode,
  id,
  onChannelsChange,
}: NotificationChannelListProps) {
  const [selectedChannels, setSelectedChannels] = useState<Channel[]>([])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const toast = useToast()

  const { findSubscription } = useNotificationSubscriptionStore()
  const { channels, fetchChannels } = useNotificationChannelStore()

  const loadSubscription = async () => {
    try {
      const result = await findSubscription(featureCode, id)
      if (result.success && result.data) {
        const subscriptionChannelIds = result.data.channels.map((c) => c.id)
        const fullChannelData = channels.filter((c) =>
          subscriptionChannelIds.includes(c.id),
        )
        setSelectedChannels(fullChannelData)
        if (onChannelsChange) {
          onChannelsChange(subscriptionChannelIds)
        }
      }
    } catch (error) {
      console.error("Error loading subscription:", error)
    }
  }

  useEffect(() => {
    fetchChannels()
  }, [fetchChannels])

  useEffect(() => {
    if (channels.length > 0) {
      loadSubscription()
    }
  }, [id, channels])

  const handleSaveChannels = (selectedChannelIds: string[]) => {
    const fullChannelData = channels.filter((c) =>
      selectedChannelIds.includes(c.id),
    )
    setSelectedChannels(fullChannelData)
    if (onChannelsChange) {
      onChannelsChange(selectedChannelIds)
    }
  }

  return (
    <Box bg="white" rounded="md" shadow="sm">
      <SelectNotificationChannelDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSaveChannels}
        featureCode={featureCode}
        subscriptionId={id}
      />

      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between">
          <Heading size="md">通知方式設定</Heading>
          <Button
            leftIcon={<Plus size={16} />}
            size="sm"
            colorScheme="blue"
            onClick={() => setIsDialogOpen(true)}>
            {selectedChannels.length === 0 ? "選擇通知方式" : "編輯通知方式"}
          </Button>
        </HStack>

        {selectedChannels.length === 0 ? (
          <Text color="gray.500" textAlign="center" py={4}>
            尚未選擇任何通知方式
          </Text>
        ) : (
          <VStack align="stretch" spacing={2}>
            {selectedChannels.map((channel) => (
              <Box
                key={channel.id}
                p={4}
                border="1px"
                borderColor="gray.200"
                rounded="md"
                _hover={{ bg: "gray.50" }}>
                <HStack justify="space-between">
                  <HStack>
                    <Badge colorScheme={getChannelTypeColor(channel.type)}>
                      {channel.type.toUpperCase()}
                    </Badge>
                    <Text>{channel.name}</Text>
                  </HStack>
                  {!channel.isActive && <Badge colorScheme="red">未啟用</Badge>}
                </HStack>
              </Box>
            ))}
          </VStack>
        )}
      </VStack>
    </Box>
  )
}
